import { omit, cloneDeep, crc32, deepEqual } from '../../lib/utils';
import { IShadow, ITextShadow } from '../domain/style';
import { IFontStyle } from '../domain/font';
import * as N from './nodes';
import { Color } from './color';
import { deserializePropertyValue, serializePropertyValue, serializeStyle2 } from './serializer';
import { IText, IRenderedSpans, OneOfRenderedSpans, SpanType, HorizontalAlignment, VerticalAlignment, OneOfSpans, OneOfContentSpans, ISpans, ISpan, IStyleIdMap, ISpanProperties, IVariable, ICharacterProperties, OneOfCharacterProperty, PreviousStyleIdType } from '../domain/text';
import { IVersionedText, IInlineStyledText, ITextSpan } from '../domain/version';
import { IColor } from '../domain';

export const characterProperties = [
    'textColor',
    'uppercase',
    'underline',
    'strikethrough',
    'fontSize',
    'font',
] as (
    'textColor' |
    'uppercase' |
    'underline' |
    'strikethrough' |
    'fontSize' |
    'font'
)[];

export const enum CharacterCodes {
    At = 0x40,
    LineFeed = 0x0A,              // \n
    CarriageReturn = 0x0D,        // \r
    LineSeparator = 0x2028,
    ParagraphSeparator = 0x2029,
    NextLine = 0x0085,

    // Unicode 3.0 space characters
    Space = 0x0020,   // " "
    NonBreakingSpace = 0x00A0,   //
    EnQuad = 0x2000,
    EmQuad = 0x2001,
    EnSpace = 0x2002,
    EmSpace = 0x2003,
    ThreePerEmSpace = 0x2004,
    FourPerEmSpace = 0x2005,
    SixPerEmSpace = 0x2006,
    FigureSpace = 0x2007,
    PunctuationSpace = 0x2008,
    ThinSpace = 0x2009,
    HairSpace = 0x200A,
    ZeroWidthSpace = 0x200B,
    NarrowNoBreakSpace = 0x202F,
    IdeographicSpace = 0x3000,
    MathematicalSpace = 0x205F,
    Ogham = 0x1680,

    // Other white space
    Backspace = 0x08,             // \b
    FormFeed = 0x0C,              // \f
    ByteOrderMark = 0xFEFF,
    Tab = 0x09,                   // \t
    VerticalTab = 0x0B,           // \v
}

export function isSpaceLikeCharacter(ch: number): boolean {
    return ch === CharacterCodes.Space ||
        ch === CharacterCodes.Tab ||
        ch === CharacterCodes.VerticalTab ||
        ch === CharacterCodes.FormFeed ||
        ch === CharacterCodes.NextLine ||
        ch === CharacterCodes.Ogham ||
        ch >= CharacterCodes.EnQuad && ch <= CharacterCodes.ZeroWidthSpace ||
        ch === CharacterCodes.MathematicalSpace ||
        ch === CharacterCodes.IdeographicSpace ||
        ch === CharacterCodes.ByteOrderMark;
}

export function isContentSpan(span?: OneOfRenderedSpans): span is OneOfContentSpans {
    return span !== undefined && (span.type ===  SpanType.Word || span.type ===  SpanType.Space || span.type === SpanType.Newline || span.type === SpanType.Variable);
}

export function isVersionedTextContentSpan(span: ITextSpan): boolean {
    return span.type ===  SpanType.Word || span.type ===  SpanType.Space || span.type === SpanType.Newline || span.type === SpanType.Variable;
}

export function isNewlineLikeCharacter(ch: number): boolean {
    return ch === CharacterCodes.LineFeed || ch === CharacterCodes.CarriageReturn;
}

export function getSpanType(text: string): SpanType {
    const ch = text.charCodeAt(0);
    if (isSpaceLikeCharacter(ch)) {
        return SpanType.Space;
    }
    if (isNewlineLikeCharacter(ch)) {
        return SpanType.Newline;
    }
    if (ch === CharacterCodes.At) {
        return SpanType.Variable;
    }
    return SpanType.Word;
}

export function forEachSpan(text: IRenderedSpans, callback: (textNode: OneOfRenderedSpans, index: number) => void): void {
    if (text && text.spans) {
        let lastIndex = text.spans.length - 1;
        if (text.spans[lastIndex].type === SpanType.End) {
            lastIndex--;
        }
        for (let i = 0; i < text.spans.length; i++) {
            callback(text.spans[i], i);
        }
    }
}

export function styleIdsAreEqual(source: IStyleIdMap, target: IStyleIdMap): boolean {
    if (source.size !== source.size) {
        return false;
    }
    for (const [documentId, sourceStyleId] of source.entries()) {
        const targetStyleId = target.get(documentId);
        if (sourceStyleId !== targetStyleId) {
            return false;
        }
    }
    return true;
}

export function toStringFromText(textRoot: IText): string {
    return '';
}

export function getHorizontalFlexAlignment(horizontalAlignment: HorizontalAlignment): string {
    switch (horizontalAlignment) {
        case 'left':
            return 'flex-start';
        case 'center':
            return 'center';
        case 'right':
            return 'flex-end';
        case 'justify':
            return 'flex-start';
    }
    throw new Error(`Invalid horizontal alignment '${horizontalAlignment}'.`);
}

export function getVerticalFlexAlignment(verticalAlignment: VerticalAlignment): string {
    switch (verticalAlignment) {
        case 'top':
            return 'flex-start';
        case 'middle':
            return 'center';
        case 'bottom':
            return 'flex-end';
    }
    throw new Error(`Invalid vertical alignment '${verticalAlignment}'.`);
}

export function createRichTextFromString(str: string): IText {
    return {
        style: N.createDefaultTextProperties(),
        spans: createSpansFromString(str).concat([{ type: SpanType.End, content: 'END', top: 0, left: 0, width: 0, height: 0, lineHeight: 0, attributes: {} }]),
        __numberOfSpans: 0,
    };
}

export function createRichTextVariableFromString(str: string, attributes: ISpanProperties = {}, style?: Partial<ICharacterProperties>): IText {
    return {
        style: N.createDefaultTextProperties(),
        spans: createSpansFromString(str, attributes, style)
            .concat([{ type: SpanType.End, content: 'END', top: 0, left: 0, width: 0, height: 0, lineHeight: 0, attributes: {} }]),
        __numberOfSpans: 0,
    };
}

export function createSpansFromString(str: string, properties: ISpanProperties = {}, style?: Partial<ICharacterProperties>, copyStyle?: (style: Partial<ICharacterProperties>) => Partial<ICharacterProperties>, styleId?: string, styleIds?: IStyleIdMap, previousStyleIds?: (string | PreviousStyleIdType)[], previousStyleIdToHistoryIndexMap?: Map<string, number>): OneOfSpans[] {
    const spans: OneOfSpans[] = [];
    let currentTextSpan: OneOfSpans | undefined;
    let lastSpanType: SpanType | undefined;
    str = (typeof str === 'number' ? (str as number).toString() : str);
    for (let i = 0; i < str.length; i++) {
        const ch = str.charCodeAt(i);
        if (isSpaceLikeCharacter(ch)) {
            if (lastSpanType === SpanType.Space) {
                (currentTextSpan as ISpan).content += String.fromCharCode(ch);
            }
            else {
                currentTextSpan = {
                    top: 0,
                    left: 0,
                    type: SpanType.Space,
                    content: String.fromCharCode(ch),
                    attributes: cloneDeep(properties.attributes || {}),
                    style: style ? copyStyle!(style) : {},
                    width: 0,
                    height: 0,
                    lineHeight: 0,
                    styleIds: styleIds || new Map<string, string>(),
                    styleId,
                    __previousStyleIds: [...previousStyleIds || []],
                    __previousStyleIdToHistoryIndexMap: new Map<string, number>(previousStyleIdToHistoryIndexMap || []),
                };
                spans.push(currentTextSpan);
                lastSpanType = SpanType.Space;
            }
        }
        else if (isNewlineLikeCharacter(ch)) {
            currentTextSpan = {
                top: 0,
                left: 0,
                type: SpanType.Newline,
                content: String.fromCharCode(ch),
                width: 0,
                height: 0,
                lineHeight: 0,
                style: {},
                attributes: cloneDeep(properties.attributes || {}),
                styleIds: styleIds || new Map<string, string>(),
                styleId,
                __previousStyleIds: [...previousStyleIds || []],
                __previousStyleIdToHistoryIndexMap: new Map<string, number>(previousStyleIdToHistoryIndexMap || []),
            };
            spans.push(currentTextSpan);
            lastSpanType = SpanType.Newline;
        }
        else {
            if (lastSpanType === SpanType.Word || lastSpanType === SpanType.Variable) {
                (currentTextSpan as ISpan).content += String.fromCharCode(ch);
            }
            else {
                if (style && style.variable) {
                    currentTextSpan = {
                        type: SpanType.Variable,
                        content: String.fromCharCode(ch),
                        style: style ? copyStyle ? copyStyle!(style) : style : {},
                        top: 0,
                        left: 0,
                        width: 0,
                        height: 0,
                        lineHeight: 0,
                        attributes: cloneDeep(properties.attributes || {}),
                        styleIds: styleIds || new Map<string, string>(),
                        styleId,
                        __previousStyleIds: [...previousStyleIds || []],
                        __previousStyleIdToHistoryIndexMap: new Map<string, number>(previousStyleIdToHistoryIndexMap || []),
                    };
                }
                else {
                    currentTextSpan = {
                        type: SpanType.Word,
                        content: String.fromCharCode(ch),
                        style: style ? copyStyle!(style) : {},
                        top: 0,
                        left: 0,
                        width: 0,
                        height: 0,
                        lineHeight: 0,
                        attributes: cloneDeep(properties.attributes || {}),
                        styleIds: styleIds || new Map<string, string>(),
                        styleId,
                        __previousStyleIds: [...previousStyleIds || []],
                        __previousStyleIdToHistoryIndexMap: new Map<string, number>(previousStyleIdToHistoryIndexMap || []),
                    };
                }
                spans.push(currentTextSpan);
                lastSpanType = style && style.variable ? SpanType.Variable : SpanType.Word;
            }
        }
    }
    for (const span of spans) {
        span.attributes.shouldRenderNumber = false;
    }
    return spans;
}

export function remapStyles(text: IVersionedText, oldDocumentId: string, newDocumentId: string): IVersionedText {
    for (const span of text.styles) {
        const oldStyleIds = new Map([...span.styleIds]);
        for (const [documentId, styleId] of oldStyleIds) {
            if (documentId === oldDocumentId) {
                span.styleIds.set(newDocumentId, styleId);
            }
        }
    }
    return text;
}

export function deserializeVersionedText(text: string): IVersionedText {
    let versionedText: ISerializedVersionedText;

    // Try to serialize
    try {
        versionedText = JSON.parse(text) as ISerializedVersionedText;
    }
    catch (e) {
        const msg = `Could not deserialize text: '${ text }'`;

        // JSON is provided but could not parse anyway
        if (text !== undefined && text.charAt(0) === '{' || text.charAt(0) === '<') {
            throw new Error(msg);
        }
        else if (process.env.STUDIO_JS) {
            if (typeof (self as any).SentryHandler !== 'undefined') {
                (self as any).SentryHandler.captureError(msg);
            }
        }
        versionedText = { text: text, styles: [] };
    }
    const remappedSpans = versionedText.styles.map((span) => {
        const styleIdMap: IStyleIdMap = new Map<string, string>();
        for (const style of span.styleIds) {
            styleIdMap.set(style.documentId, style.styleId);
        }
        return {
            ...span,
            styleIds: styleIdMap,
        };
    });
    return {
        text: versionedText.text,
        styles: remappedSpans,
    };
}

export function isVersionedText(text: any): boolean {
    let isVersionedProperty = false;
    if (typeof text === 'string') {
        try {
            const parsed = JSON.parse(text) as IVersionedText;
            if (parsed.text !== undefined && parsed.styles !== undefined) {
                isVersionedProperty = true;
            }
        }
        catch (e) { }
    }
    else {
        try {
            if (typeof text === 'object' && text.text !== undefined && text.styles !== undefined) {
                isVersionedProperty = true;
            }
        }
        catch (e) { }
    }

    return isVersionedProperty;
}

export function deserializeInlineStyledText(text: string): IInlineStyledText {
    const inlineStyledText = JSON.parse(text) as ISerializedInlineStyledText;
    const remappedSpans = inlineStyledText.styles.map((span) => {
        return {
            ...span,
            style: deserializeStyle(span.style),
        };
    });
    return {
        text: inlineStyledText.text,
        styles: remappedSpans,
    };
}

export function serializeStyle(style: Partial<ICharacterProperties>): string {
    const styleText: string[] = [];
    for (const property in style) {
        if (property === '__fontFamilyId') {
            continue;
        }
        const value = style[property];
        if (value !== undefined) {
            styleText.push(`${property}:${serializePropertyValue(property === 'variable' ? 'feed' : property, value)}`);
        }
    }
    return styleText.sort().join(';');
}

export function deserializeStyle(text: string): Partial<ICharacterProperties> {
    const properties: [string, string][] = text.split(';').map(p => p.split(':')) as [string, string][];
    const style: Partial<ICharacterProperties> = {};
    for (const property of properties) {
        style[property[0]] = deserializePropertyValue(property[0] === 'variable' ? 'feed' : property[0], property[1]);
    }
    return style;
}

export function deserializeElementStyle(element: HTMLElement): Partial<ICharacterProperties> {
    const propertiesToUse = element.dataset.studioValues ? element.dataset.studioValues.split(' ') : [];

    const style: Partial<ICharacterProperties> = {};
    for (const property of propertiesToUse) {
        switch (property as OneOfCharacterProperty) {
            case 'font':
                const font = deserializePropertyValue('font', element.dataset.font) as IFontStyle;
                style.font = font;
                break;
            case 'textColor':
                style.textColor = deserializePropertyValue('color', element.style.color) as IColor;
                break;
            case 'fontSize':
                style.fontSize = deserializePropertyValue('number', element.style.fontSize) as number;
                break;
            case 'strikethrough':
            case 'underline':
                if (element.style.textDecoration!.indexOf('line-through') > -1) {
                    style.strikethrough = true;
                }
                if (element.style.textDecoration!.indexOf('underline') > -1) {
                    style.underline = true;
                }
                break;
            case 'uppercase':
                style.uppercase = element.style.textTransform!.indexOf('uppercase') > -1;
                break;
            case 'characterSpacing':
                style.characterSpacing = deserializePropertyValue('number', element.style.letterSpacing) as number;
                break;
            case 'lineHeight':
                style.lineHeight = deserializePropertyValue('number', element.style.lineHeight) as number;
                break;
            case 'textShadows':
                style.textShadows = deserializePropertyValue('textShadows', element.style.textShadow) as ITextShadow[];
                break;
            case 'variable':
                style.variable = deserializePropertyValue('feed', element.dataset.variable) as IVariable;
        }
    }
    return style;
}

interface ISerializedStyleMap {
    documentId: string;
    styleId: string;
}

interface ISerializedTextSpan {
    type: SpanType;
    position: number;
    length: number;
    styleIds: ISerializedStyleMap[];
    variable?: IVariable;
}

interface ISerializedInlineStyledTextSpan {
    type: SpanType;
    position: number;
    length: number;
    style: string;
}

interface ISerializedInlineStyledText {
    text: string;
    styles: ISerializedInlineStyledTextSpan[];
}

interface ISerializedVersionedText {
    text: string;
    styles: ISerializedTextSpan[];
}

export function hasSameStyleIds(source: IStyleIdMap, target: IStyleIdMap): boolean {
    if (source.size !== target.size) {
        return false;
    }
    for (const [sourceDesignId, sourceStyleId] of source.entries()) {
        if (!(target.has(sourceDesignId) && target.get(sourceDesignId) === sourceStyleId)) {
            return false;
        }
    }
    return true;
}

export function serializeVersionedText(text: IVersionedText): string {
    const spans: ISerializedTextSpan[] = [];
    for (const span of text.styles) {
        if (span.type === SpanType.End) {
            break;
        }
        const styleIds: ISerializedStyleMap[] = [];
        for (const [documentId, styleId] of span.styleIds.entries()) {
            styleIds.push({ documentId, styleId });
        }

        spans.push({
            type: span.type,
            length: span.length,
            position: span.position,
            styleIds: styleIds.sort((a, b) => {
                if (a.documentId > b.documentId) {
                    return 1;
                }
                else if (a.documentId < b.documentId) {
                    return -1;
                }
                else if (a.styleId > b.styleId) {
                    return 1;
                }
                else if (a.styleId < b.styleId) {
                    return -1;
                }
                throw new Error('StyleId is not unique');
            }),
            variable: span.variable,
        });
    }
    return JSON.stringify({ text: text.text, styles: spans});
}

export function copyVersionedText(text: IVersionedText): IVersionedText {
    return deserializeVersionedText(serializeVersionedText(text));
}

export function serializeInlineStyledText(text: IInlineStyledText): string {
    const spans: ISerializedInlineStyledTextSpan[] = [];
    for (const span of text.styles) {
        if (span.type === SpanType.End) {
            break;
        }
        spans.push({
            type: span.type,
            length: span.length,
            position: span.position,
            style: serializeStyle(span.style),
        });
    }
    return JSON.stringify({ text: text.text, styles: spans } as ISerializedInlineStyledText);
}

/**
 * Get raw string from IText (without formatting)
 * @param text
 * @param tw
 */
export function getTextContent(text: ISpans): string {
    let str = '';
    outer: for (const span of text.spans) {
        switch (span.type) {
            case SpanType.Newline:
                str += '\n';
                break;
            case SpanType.Space:
            case SpanType.Word:
                str += span.content;
                break;
            case SpanType.Variable:
                str += span.content;
                break;
            case SpanType.End:
                break outer;
            default:
                throw new Error('Unknown span type.');
        }
    }
    return str;
}

export function textSpanIsMergeable(source: ITextSpan, target: ITextSpan, checkPreviousStyles: boolean): boolean {
    if (source.type === SpanType.Newline || source.type === SpanType.End) {
        return false;
    }
    if (source.type !== target.type) {
        return false;
    }
    if (!hasSameStyleIds(source.styleIds, target.styleIds)) {
        return false;
    }
    if (checkPreviousStyles) {
        if (!deepEqual(source.__previousStyleIds, target.__previousStyleIds)) {
            return false;
        }
    }
    return true;
}

export function isSubStyleOf(source: Partial<ICharacterProperties>, target: Partial<ICharacterProperties>): boolean {
    const sourceStyle = omit(source, 'variable');
    const targetStyle = omit(target, 'variable');
    if (Object.keys(sourceStyle).length < Object.keys(targetStyle).length) {
        return false;
    }
    for (const property in targetStyle) {
        if (!hasSameStyleProperty(property as keyof ICharacterProperties, targetStyle, sourceStyle)) {
            return false;
        }
    }
    return true;
}

export function hasSameStyle(source: Partial<ICharacterProperties>, target: Partial<ICharacterProperties>): boolean {
    const sourceStyle = omit(source, 'variable', '__fontFamilyId');
    const targetStyle = omit(target, 'variable', '__fontFamilyId');
    if (Object.keys(sourceStyle).length !== Object.keys(targetStyle).length) {
        return false;
    }
    for (const property in sourceStyle) {
        if (!hasSameStyleProperty(property as keyof ICharacterProperties, sourceStyle, targetStyle)) {
            return false;
        }
    }
    return true;
}

export function hasSameStyleProperty(property: string, source: Partial<ICharacterProperties>, target: Partial<ICharacterProperties>, treatMissingAsFalse: boolean = true): boolean {
    const targetValue = target[property];
    if (targetValue === '$mixed') {
        return false;
    }
    const sourceValue = source[property]!;
    switch (property) {
        case 'uppercase':
        case 'underscore':
        case 'strikethrough':

            // We need to distinguish between 'missing' and 'false' values sometimes. An example, is when we want to promote commonly
            // styled properties across whole text to element styles. A 'uppercase: false' is not the same as 'uppercase: undefined'.
            // If we have in the first span 'uppercase: false' and all the rest of the spans has 'uppercase: undefined'. We don't want
            // to promote 'uppercase: false' to element style.
            if (!treatMissingAsFalse) {
                return sourceValue === targetValue;
            }
            return !!sourceValue === !!targetValue;

        case 'font': {
            const target = targetValue as IFontStyle;
            const source = sourceValue as IFontStyle;
            if (source === target) {
                return true;
            }
            if (target === undefined || source === undefined) {
                return false;
            }
            return target.id === source.id &&
                target.fontFamilyId === source.fontFamilyId &&
                target.style === source.style &&
                target.weight === source.weight &&
                target.src === source.src;
        }
        case 'textColor':
            if (sourceValue === targetValue) {
                return true;
            }
            if (sourceValue === undefined || targetValue === undefined) {
                return false;
            }
            if (!(sourceValue instanceof Color && targetValue instanceof Color)) {
                throw new Error(`Neither 'targetValue' nor 'sourceValue' is of type Color.`);
            }
            return sourceValue.toString() === targetValue!.toString();
        case 'textShadows': {
            const targetShadows = sourceValue as IShadow[];
            const sourceShadows = targetValue as IShadow[];
            if (typeof targetShadows !== typeof sourceShadows) {
                return false;
            }
            if (targetShadows === sourceShadows) {
                return true;
            }
            if (targetShadows.length !== sourceShadows.length) {
                return false;
            }
            for (let i = 0; i < targetShadows.length; i++) {
                const source = sourceShadows[i];
                const target = targetShadows[i];
                const isSame = source.blur === target.blur &&
                    source.color === target.color &&
                    source.offsetX === target.offsetX &&
                    source.offsetY === target.offsetY &&
                    source.spread === target.spread;

                if (!isSame) {
                    return false;
                }
            }
            return true;
        }
        default:
            return sourceValue === targetValue;
    }
}

export function getMaxIndexFromStyleMap(styleIndexMap: Map<string, number>): number {
    let maxIndex = 0;
    for (const index of styleIndexMap.values()) {
        if (index > maxIndex) {
            maxIndex = index;
        }
    }
    return maxIndex;
}

interface IStyleIndexMaps {
    styleHashToIndexMap: Map<string, number>;
    indexToStyleIdsMap: Map<number, Map<string, string>>;
}
export function createStyleIndexMap(text: IVersionedText, existingStyleIndexMap?: IStyleIndexMaps): IStyleIndexMaps {
    const styleHashToIndexMap = existingStyleIndexMap && existingStyleIndexMap.styleHashToIndexMap || new Map<string, number>();
    const indexToStyleIdsMap = existingStyleIndexMap && existingStyleIndexMap.indexToStyleIdsMap || new Map<number, Map<string, string>>();
    let currentStyleIndex = existingStyleIndexMap
        ? getMaxIndexFromStyleMap(existingStyleIndexMap.styleHashToIndexMap)
        : 0;

    for (let i = 0; i < text.styles.length; i++) {
        const span = text.styles[i];
        switch (span.type) {
            case SpanType.Word:
            case SpanType.Space:
            case SpanType.Variable:
                if (span.styleIds.size === 0) {
                    continue;
                }
                let styleIndex: number | undefined;
                const sequencedStyleIds = sequenceStyleIds(span.styleIds);
                styleIndex = styleHashToIndexMap.get(sequencedStyleIds);
                if (styleIndex === undefined) {
                    currentStyleIndex++;
                    styleIndex = currentStyleIndex;
                }
                indexToStyleIdsMap.set(styleIndex, new Map<string, string>(span.styleIds));
                styleHashToIndexMap.set(sequencedStyleIds, styleIndex);
                continue;
        }
    }

    return { styleHashToIndexMap, indexToStyleIdsMap };
}

export function sequenceStyleIds(styleIds: IStyleIdMap): string {
    let serializedStyle = '';
    const sortedStyleIds = Array.from(styleIds.values()).sort();
    for (const styleId of sortedStyleIds) {
        serializedStyle += styleId;
    }
    return serializedStyle;
}

export function getHashFromStyle(style: Partial<ICharacterProperties>): string {
    return crc32(sequenceStyle(style));
}

export function getCommonStyledPropertiesFromUniqueOnes(uniquelyStyledProperties: Set<string>, style: Partial<ICharacterProperties>): Set<string> {
    return new Set(Object.keys(style)
        .filter(property => !uniquelyStyledProperties.has(property)));
}

export function sequenceStyle(style: Partial<ICharacterProperties>): string {
    let styleSequencedString = '';
    for (const property of N.textProperties) {
        if (!style) {
            continue;
        }
        if (property === '__fontFamilyId') {
            continue;
        }
        const styleValue = style[property];
        if (styleValue === undefined) {
            continue;
        }
        if (property === 'font') {
            styleSequencedString += property + (typeof styleValue === 'string' ? styleValue : (styleValue as IFontStyle).id);
        }
        else if (styleValue !== undefined) {
            styleSequencedString += property + serializeStyle2({ [property]: styleValue })[property];
        }
    }
    return styleSequencedString;
}
